/**
 * Check Request Status Page Component
 * Allows users to check the status of their access request
 */

import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Input, Message, Card } from '../components/ui';
import './CheckRequestStatus.css';

const CheckRequestStatus = () => {
  const [searchParams] = useSearchParams();
  const initialRequestId = searchParams.get('id') || '';

  const [requestId, setRequestId] = useState(initialRequestId);
  const [statusData, setStatusData] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    if (type === 'error') {
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!requestId.trim()) {
      showMessage('Please enter a request ID', 'error');
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', type: '' });
    setStatusData(null);

    try {
      const response = await fetch(`/api/access/request/${requestId.trim()}/status`);

      if (response.status === 404) {
        showMessage('Request not found. Please check your request ID.', 'error');
        return;
      }

      const data = await response.json();

      if (data.success) {
        setStatusData(data);
      } else {
        showMessage(data.error || 'Failed to fetch status', 'error');
      }
    } catch (error) {
      showMessage('Network error: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    const statusConfig = {
      pending: {
        icon: 'fas fa-clock',
        label: 'Pending Review',
        className: 'rf-status-pending',
        description: 'Your request is awaiting administrator review.'
      },
      approved: {
        icon: 'fas fa-check-circle',
        label: 'Approved',
        className: 'rf-status-approved',
        description: 'Your request has been approved! Check your email for login credentials.'
      },
      'auto-approved': {
        icon: 'fas fa-check-circle',
        label: 'Auto-Approved',
        className: 'rf-status-approved',
        description: 'Your request was automatically approved. You should have received your credentials.'
      },
      rejected: {
        icon: 'fas fa-times-circle',
        label: 'Rejected',
        className: 'rf-status-rejected',
        description: 'Your request was not approved. Please contact an administrator for more information.'
      }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  return (
    <div className="rf-check-status-page">
      <div className="rf-center-container">
        <Card className="rf-status-card">
          <div className="rf-auth-logo">
            <i className="fas fa-search"></i>
            <span>Check Request Status</span>
          </div>

          <p className="rf-status-description">
            Enter your request ID to check the status of your access request.
          </p>

          <form onSubmit={handleSubmit} className="rf-status-form">
            <Input
              label="Request ID"
              type="text"
              name="requestId"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              placeholder="Enter your request ID"
              autoFocus
            />

            <Button
              type="submit"
              variant="primary"
              block
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Checking...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Check Status
                </>
              )}
            </Button>
          </form>

          {message.text && (
            <Message type={message.type}>{message.text}</Message>
          )}

          {statusData && (
            <div className="rf-status-result">
              <div className={`rf-status-badge ${getStatusDisplay(statusData.status).className}`}>
                <i className={getStatusDisplay(statusData.status).icon}></i>
                <span>{getStatusDisplay(statusData.status).label}</span>
              </div>

              <p className="rf-status-info">
                {getStatusDisplay(statusData.status).description}
              </p>

              <div className="rf-status-details">
                <div className="rf-detail-item">
                  <label>Email:</label>
                  <span>{statusData.email}</span>
                </div>
                <div className="rf-detail-item">
                  <label>Submitted:</label>
                  <span>{new Date(statusData.submittedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
                {statusData.processedAt && (
                  <div className="rf-detail-item">
                    <label>Processed:</label>
                    <span>{new Date(statusData.processedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                  </div>
                )}
              </div>

              {(statusData.status === 'approved' || statusData.status === 'auto-approved') && (
                <Link to="/login" className="rf-btn rf-btn-primary rf-btn-block">
                  <i className="fas fa-sign-in-alt"></i>
                  Go to Login
                </Link>
              )}
            </div>
          )}

          <div className="rf-status-footer">
            <div className="rf-footer-links">
              <Link to="/login" className="rf-link">
                <i className="fas fa-sign-in-alt"></i>
                Back to Login
              </Link>
              <span className="rf-link-divider">|</span>
              <Link to="/request-access" className="rf-link">
                <i className="fas fa-user-plus"></i>
                Request Access
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CheckRequestStatus;
